import { CapturedNetworkRequest } from 'posthog-js';

// Session-replay network capture records request/response URLs, headers, and
// bodies, which for Tread's API routinely carry PII (names, emails, phones,
// addresses) and live credentials (the Bearer token and the
// `x-horizon-api-session-jwt` / `session_jwt` session token). posthog runs
// this `maskCapturedNetworkRequestFn` in the browser BEFORE anything is sent,
// so all redaction must happen here. It MUST NOT throw — a throw would break
// recording — so every branch is defensive and falls back to redacting rather
// than leaking. When in doubt it over-redacts: dropping a diagnostic value is
// acceptable, leaking a credential or PII is not.

const REDACTED = '[redacted]';

// Header names whose values are secrets or PII. `x-horizon-api-session-jwt` is
// the response header the connection module reads to persist the session
// (see services/connectionModule.tsx), so it is a live credential.
const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-horizon-api-session-jwt',
];

// Substrings/shapes that mark a key's VALUE as PII or a secret, matched
// case-insensitively. Broad on purpose so close variants (email_address,
// phone_number, contact_email, middle_name, street) are covered too. Personal
// names are matched only with a person-name prefix, so diagnostic keys like
// `company_name`, `filename`, and `field_name` survive.
const SENSITIVE_KEY_PATTERNS = [
  /pass(word)?/i,
  /secret/i,
  /token/i,
  /jwt/i,
  /session/i,
  /api[_-]?key/i,
  /authoriz/i,
  /email/i,
  /phone|mobile/i,
  /(^|_)ssn(_|$)|social_security/i,
  /passport/i,
  /(^|_)dob(_|$)|birth/i,
  /address|street|postal|(^|_)zip(_|$)/i,
  /(first|last|middle|full|given|family|maiden|display|user|nick|preferred)[_-]?name/i,
  /credit|(^|_)cvv(_|$)|iban|routing|account[_-]?number|(^|_)card(_|$)|card_?num/i,
  /tax[_-]?id|license/i,
];

const keyIsSensitive = (key: string) =>
  SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));

// Walk a parsed JSON value and blank the values of any sensitive-named key,
// preserving the surrounding structure so error diagnostics stay readable.
function redactJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(redactJsonValue);
  }

  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [key, nested] of Object.entries(value)) {
      result[key] = keyIsSensitive(key) ? REDACTED : redactJsonValue(nested);
    }

    return result;
  }

  return value;
}

// Redact a captured body. JSON bodies are scrubbed key-by-key; anything we
// cannot parse (form-encoded, HTML, plain text) is dropped wholesale, since we
// cannot safely tell which parts are sensitive.
function scrubBody(body: string | null | undefined) {
  if (!body) {
    return body;
  }

  try {
    return JSON.stringify(redactJsonValue(JSON.parse(body)));
  } catch {
    return REDACTED;
  }
}

function scrubHeaders(headers: CapturedNetworkRequest['requestHeaders']) {
  if (!headers) {
    return headers;
  }

  const result: Record<string, string> = {};
  for (const [name, value] of Object.entries(headers)) {
    const isSensitive =
      SENSITIVE_HEADERS.includes(name.toLowerCase()) || keyIsSensitive(name);
    result[name] = isSensitive ? REDACTED : value;
  }

  return result;
}

// Redact the values of sensitive query parameters from the captured URL, so
// tokens/emails passed on the query string are not recorded. The path and
// non-sensitive params (ids, filters) survive so the failing route stays
// identifiable. A placeholder base lets relative URLs parse; it is stripped
// back off before returning.
const RELATIVE_URL_BASE = 'http://redacted.invalid';

function scrubUrl(url: string) {
  if (!url) {
    return url;
  }

  try {
    const parsed = new URL(url, RELATIVE_URL_BASE);

    let changed = false;
    parsed.searchParams.forEach((_value, key) => {
      if (keyIsSensitive(key)) {
        parsed.searchParams.set(key, REDACTED);
        changed = true;
      }
    });

    if (!changed) {
      return url;
    }

    const wasRelative = parsed.origin === RELATIVE_URL_BASE && !/^https?:/i.test(url);

    return wasRelative ? `${parsed.pathname}${parsed.search}` : parsed.toString();
  } catch {
    return url;
  }
}

const EMAIL_INTAKE_MESSAGE_ROUTE = /\/email_intake_message(?:[?#]|$)/;

export function maskNetworkRequest(request: CapturedNetworkRequest) {
  const responseIsRawEmail = EMAIL_INTAKE_MESSAGE_ROUTE.test(request.name);

  request.name = scrubUrl(request.name);
  request.requestHeaders = scrubHeaders(request.requestHeaders);
  request.responseHeaders = scrubHeaders(request.responseHeaders);
  request.requestBody = scrubBody(request.requestBody);
  request.responseBody = responseIsRawEmail ? REDACTED : scrubBody(request.responseBody);

  return request;
}
